import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useLocation, useRoutes, type RouteObject } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import * as Sentry from "@sentry/react";
import { CMSProvider } from "@/lib/CMSContext";
import { ThemeProvider } from "@/lib/ThemeContext";
import { PageViewTracker } from "./components/PageViewTracker";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Index from "./pages/Index";
import Order from "./pages/Order";
import OrderSuccess from "./pages/OrderSuccess";
import NotFound from "./pages/NotFound";
import Admin from "./pages/Admin";
import AdminLogin from "./pages/AdminLogin";
import AdminGuard from "./components/AdminGuard";
import Dashboard from "./pages/admin/Dashboard";
import Bookings from "./pages/admin/Bookings";
import Users from "./pages/admin/Users";
import CmsEditor from "./pages/admin/CmsEditor";
import Documents from "./pages/admin/Documents";
import DocumentEdit from "./pages/admin/DocumentEdit";
import DynamicPage from "./pages/DynamicPage";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import ActivityLog from "./pages/admin/ActivityLog";

const queryClient = new QueryClient();

const appRouteConfig: RouteObject[] = [
  { path: "/", element: <Index /> },
  { path: "/order", element: <LanguageProvider><Order /></LanguageProvider> },
  { path: "/order/success", element: <LanguageProvider><OrderSuccess /></LanguageProvider> },
  { path: "/privacy", element: <Privacy /> },
  { path: "/terms", element: <Terms /> },
  { path: "/page/:slug", element: <LanguageProvider><DynamicPage /></LanguageProvider> },
  { path: "/admin/login", element: <AdminLogin /> },
  {
    path: "/admin",
    element: (
      <AdminGuard>
        <Admin />
      </AdminGuard>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: "bookings", element: <Bookings /> },
      { path: "users", element: <Users /> },
      { path: "cms", element: <CmsEditor /> },
      { path: "documents", element: <Documents /> },
      { path: "documents/:id", element: <DocumentEdit /> },
      { path: "activity", element: <ActivityLog /> },
    ],
  },
  { path: "*", element: <NotFound /> },
];

function AnimatedRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const routes = useRoutes(appRouteConfig);

  if (isAdmin) return routes;

  return (
    <AnimatePresence mode="wait">
      <div key={location.pathname}>{routes}</div>
    </AnimatePresence>
  );
}

const App = () => (
  <Sentry.ErrorBoundary fallback={<p className="p-8 text-center">Something went wrong. Please refresh the page.</p>}>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <CMSProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <PageViewTracker />
              <AnimatedRoutes />
            </BrowserRouter>
          </TooltipProvider>
        </CMSProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
